import { ArrowRight, Check, Info, Minus } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import NotFound from "@/components/NotFound";
import { ClosingBand, Crumbs, PublicHeader, breadcrumbs, jsonLd } from "@/components/UseCasePage";
import { useHeadMeta } from "@/lib/head";
import { SITE, SITE_URL } from "@/content/site";
import { ALTERNATIVES, COMPARED_ON, OUR_LIMITS, findAlternative, pathForAlternative } from "@/content/alternatives.mjs";
import "./HomepageFonts.css";
import "./Homepage.css";
import "./UseCases.css";

export default function AlternativePage({ slug }) {
  const page = findAlternative(slug);
  useHeadMeta({ title: page?.title, robots: page ? "index, follow" : "noindex, nofollow" });
  if (!page) return <NotFound />;
  const path = pathForAlternative(page.slug);
  const crumbs = [{ name: "Home", path: "/" }, { name: page.nav, path }];
  const graph = { "@context": "https://schema.org", "@graph": [
    { "@type": "WebPage", "@id": `${SITE_URL}${path}#webpage`, url: `${SITE_URL}${path}`, name: page.title, description: page.description, inLanguage: "en-GB", isPartOf: { "@id": `${SITE_URL}/#website` }, about: { "@id": `${SITE_URL}/#software` }, publisher: { "@id": `${SITE_URL}/#organization` } },
    breadcrumbs(crumbs),
  ] };
  const rows = [
    ["Main job", page.theirJob, page.ours.job],
    ["Data it uses", page.theirData, page.ours.data],
    ["What you get", page.theirOutput, page.ours.output],
    ["GA4 key events in priorities", "See their site for current details", page.ours.ga4],
    ["Questions about your data", "See their site for current details", page.ours.questions],
    ["Getting started", "See their site for current plans", page.ours.start],
  ];
  const others = ALTERNATIVES.filter(a => a.slug !== page.slug);
  return <div className="ss-home ss-uc">
    <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(graph)} />
    <a className="ss-skip" href="#main">Skip to content</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-uc-hero"><div className="ss-container">
        <Crumbs items={crumbs} />
        <p className="ss-eyebrow">{`${page.name.toUpperCase()} ALTERNATIVE`}</p>
        <h1>{page.h1}</h1>
        <p className="ss-lead">{page.lead}</p>
        <div className="ss-actions"><a className="ss-button ss-primary" href="/">Get started free <ArrowRight size={16} aria-hidden="true" /></a><a className="ss-button ss-secondary" href="/use-cases">See use cases</a></div>
      </div></section>

      <section className="ss-container ss-section" aria-labelledby="alt-compare">
        <h2 id="alt-compare">{`${page.name} and ${SITE.name} at a glance`}</h2>
        <div className="ss-alt-table-wrap" tabIndex={0} role="region" aria-labelledby="alt-compare">
          <table className="ss-alt-table">
            <thead><tr><th scope="col"><span className="ss-sr-only">Compared on</span></th><th scope="col">{page.name}</th><th scope="col">{SITE.name}</th></tr></thead>
            <tbody>{rows.map(([label, theirs, ours]) => <tr key={label}><th scope="row">{label}</th><td>{theirs}</td><td>{ours}</td></tr>)}</tbody>
          </table>
        </div>
        <p className="ss-alt-note">{`Based on public information as of ${COMPARED_ON}. Features change, so check `}<a href={page.site} rel="nofollow noopener noreferrer">{page.site.replace("https://", "")}</a>{" for current details."}</p>
      </section>

      <section className="ss-uc-band" aria-labelledby="alt-choose"><div className="ss-container">
        <h2 id="alt-choose" className="ss-alt-choose-title">Which one fits your work?</h2>
        <div className="ss-uc-two">
          <div className="ss-uc-panel">
            <h3>{`Choose ${page.name} if`}</h3>
            <ul className="ss-uc-list ss-uc-list-muted">{page.chooseThem.map(item => <li key={item}><Check size={18} aria-hidden="true" />{item}</li>)}</ul>
            <p className="ss-alt-features-title">{`${page.name} includes tools such as:`}</p>
            <ul className="ss-alt-features">{page.theirFeatures.map(item => <li key={item}>{item}</li>)}</ul>
          </div>
          <div className="ss-uc-panel">
            <h3>{`Choose ${SITE.name} if`}</h3>
            <ul className="ss-uc-list">{page.chooseUs.map(item => <li key={item}><Check size={18} aria-hidden="true" />{item}</li>)}</ul>
            <p className="ss-alt-features-title">What SEO Signals does not do:</p>
            <ul className="ss-uc-list ss-uc-list-muted ss-alt-limits">{OUR_LIMITS.map(item => <li key={item}><Minus size={18} aria-hidden="true" />{item}</li>)}</ul>
          </div>
        </div>
      </div></section>

      <section className="ss-container ss-section ss-uc-limits" aria-labelledby="alt-both">
        <span className="ss-uc-icon ss-uc-icon-muted"><Info size={22} aria-hidden="true" /></span>
        <h2 id="alt-both">Using both</h2>
        <p>{`Many teams use a research suite such as ${page.name} for keywords, competitors and links, and ${SITE.name} to decide which change to make on their own site next. ${SITE.name} only reads your own Google data, so it works alongside other tools without replacing them.`}</p>
        <p className="ss-alt-trademark">{`${page.name} is a trademark of its owner. ${SITE.name} is not affiliated with or endorsed by ${page.name}.`}</p>
      </section>

      <section className="ss-container ss-section ss-uc-related" aria-labelledby="alt-related">
        <h2 id="alt-related">Other comparisons</h2>
        <ul className="ss-uc-grid ss-uc-grid-small">{others.map(a => <li key={a.slug}><a href={pathForAlternative(a.slug)} className="ss-uc-card">
          <span className="ss-uc-card-eyebrow">{`${a.name.toUpperCase()} ALTERNATIVE`}</span>
          <h3>{a.h1}</h3>
          <span className="ss-uc-card-more">Read more <ArrowRight size={16} aria-hidden="true" /></span>
        </a></li>)}</ul>
      </section>
      <ClosingBand />
    </main>
    <SiteFooter />
  </div>;
}
